import { ColumnDef } from '@tanstack/react-table'
import { ChainsSummary } from 'models/summaries'
import { Preferences } from 'models/preferences'
import { SortOption } from 'components/Buttons/TableButtons/TableButtonSort'
import { ReactElement } from 'react'
import { PiXCircle } from 'react-icons/pi'
import { DataTableColumnSort } from '../../DataTable/DataTableSorting/DataTableSortingStore'
import {
  sortableFieldOptions as chainsReportsSortableFieldOptions,
  sortableFields as chainsReportsSortableFields,
  useChainsTableColumnsV2,
} from './ChainsTableColumnsV2'

/**
 * The shared shape for the Chains table variant. Only one variant remains —
 * the CRM-reports table — after the legacy V1 `restaurants-bars` variant was
 * removed. The tri-mode elastic search, the `setChainsSummary` header, the menu
 * modal, the product selector, the location columns/filters, and the full
 * action set all live once in the shared `<ChainsTableComponent>`. The reports
 * variant reads rows + summary from the `reports/chains/` endpoint.
 */
export interface ChainsTableVariant {
  /** tableType slug for tags/distributors/GPOs/campaign/export/deal actions. */
  tableTypeSlug: 'restaurants-bars' | 'chains-reports-table'
  /** Per-variant columns hook (keys `ChainProxyReport`). */
  useColumns: (
    chainsSummary: ChainsSummary | undefined,
    setMenuModal: any,
    sorting: DataTableColumnSort[] | undefined,
    data: any,
    selectedProducts: number[],
    preferences: Preferences | undefined,
    inCampaigns?: boolean
  ) => ColumnDef<any, any>[]
  sortableFields: readonly string[]
  sortableFieldOptions: readonly SortOption<string>[]
  /**
   * Forward the previous summary (with `isLoading: true`) during a refetch to
   * avoid a flicker.
   */
  preserveSummaryWhileLoading: boolean
  /** Icon for the "Unassign from Campaign" action. */
  unassignIcon: ReactElement
}

/** The CRM-reports chains table — the only variant. */
export const chainsReportsTableVariant: ChainsTableVariant = {
  tableTypeSlug: 'chains-reports-table',
  useColumns: useChainsTableColumnsV2,
  sortableFields: chainsReportsSortableFields,
  sortableFieldOptions: chainsReportsSortableFieldOptions,
  preserveSummaryWhileLoading: true,
  unassignIcon: <PiXCircle size={20} />,
}
